import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/kielo";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { BIOME_IMG } from "@/i18n/extra";
import { Check, ArrowRight, Search, BookOpen, ListChecks, MapPin, Lock } from "lucide-react";

const LEVELS = [
  { key: "all", labelKey: "flash.all", testid: "filter-level-all" },
  { key: "A1", label: "A1", testid: "filter-level-a1" },
  { key: "A2", label: "A2", testid: "filter-level-a2" },
  { key: "B1", label: "B1", testid: "filter-level-b1" },
];

const TONE = {
  A1: { main: "#0E382B", tint: "#E8F0EC" },
  A2: { main: "#1E429F", tint: "#EDF2FE" },
  B1: { main: "#1A6B72", tint: "#E3F2F1" },
};

function BiomeBanner({ level, done, total }) {
  const { t } = useI18n();
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="relative overflow-hidden rounded-3xl h-48 md:h-56 ss-grain" data-testid={`biome-banner-${level}`}>
      <img src={BIOME_IMG[level]} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0b2018]/90 via-[#0b2018]/60 to-transparent" />
      <div className="relative h-full flex flex-col justify-end p-6 md:p-8 text-white max-w-xl">
        <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#F6C9AD]">{t(`journey.${level}sub`)}</span>
        <h2 className="font-serif text-3xl md:text-4xl font-semibold mt-1">{t(`journey.${level}`)}</h2>
        <div className="mt-4 flex items-center gap-3">
          <div className="h-2 w-40 rounded-full bg-white/20 overflow-hidden">
            <div className="h-full bg-[#E46A28] transition-[width] duration-700" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-sm text-white/85">{done}/{total} {t("journey.done")}</span>
        </div>
      </div>
    </div>
  );
}

function PathNode({ l, idx, done, current }) {
  const { t } = useI18n();
  const right = idx % 2 === 1;
  const tone = TONE[l.level] || TONE.A1;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className={`relative pl-16 md:pl-0 md:w-1/2 ${right ? "md:ml-auto md:pl-14" : "md:pr-14"}`}
    >
      <span
        className={`absolute top-6 left-6 -translate-x-1/2 md:translate-x-0 ${right ? "md:-left-5" : "md:left-auto md:-right-5"} z-10 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-4 border-[#F9F8F5] ${current ? "ss-ping" : ""}`}
        style={{ backgroundColor: done ? tone.main : current ? "#E46A28" : "#fff", color: done || current ? "#fff" : tone.main, boxShadow: "0 0 0 1px #E2DFD7" }}
      >
        {done ? <Check className="w-4 h-4" /> : l.order}
      </span>
      <Link
        to={`/lesson/${l.id}`}
        data-testid={`lesson-card-${l.id}`}
        className={`group block bg-white rounded-2xl border p-5 md:p-6 hover:-translate-y-1 hover:shadow-[0_14px_30px_rgba(14,56,43,0.10)] transition-[transform,box-shadow] duration-300 ${current ? "border-[#E46A28] ring-4 ring-[#FDEEE5]" : "border-[#E2DFD7]"}`}
      >
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-[11px] font-bold tracking-wider px-2.5 py-1 rounded-full" style={{ backgroundColor: tone.tint, color: tone.main }}>
            {l.level} · {String(l.order).padStart(2, "0")}
          </span>
          {done && (
            <span className="text-xs font-semibold text-[#0E5A35] flex items-center gap-1" data-testid={`lesson-completed-badge-${l.id}`}>
              <Check className="w-3.5 h-3.5" /> {t("journey.done")}
            </span>
          )}
          {current && !done && (
            <span className="text-xs font-semibold text-[#E46A28] flex items-center gap-1 animate-float" data-testid="journey-current-marker">
              <MapPin className="w-3.5 h-3.5" /> {t("journey.here")}
            </span>
          )}
        </div>
        <h3 className="font-serif text-xl font-semibold text-[#16201B] leading-snug group-hover:text-[#0E382B]">{l.title}</h3>
        <p className="mt-1 text-sm text-[#52635B]">{l.subtitle}</p>
        <div className="mt-4 pt-3 border-t border-[#F1EFEB] flex items-center justify-between text-xs text-[#52635B]">
          <span className="flex items-center gap-3 min-w-0">
            <span className="inline-flex items-center gap-1 truncate"><BookOpen className="w-3.5 h-3.5 shrink-0" /> {l.grammarFocus}</span>
            {l.quizCount > 0 && <span className="inline-flex items-center gap-1 shrink-0"><ListChecks className="w-3.5 h-3.5" /> {l.quizCount}</span>}
          </span>
          <span className="inline-flex items-center gap-1 font-semibold text-[#E46A28] shrink-0">
            {done ? t("journey.review") : t("journey.start")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

export default function LessonBrowse() {
  const { progress } = useApp();
  const { t, lang } = useI18n();
  const [lessons, setLessons] = useState([]);
  const [level, setLevel] = useState("all");
  const [query, setQuery] = useState("");
  const completed = new Set(progress?.completed_lessons || []);

  useEffect(() => {
    api.get("/curriculum", { params: { lang } }).then(({ data }) => setLessons(data));
  }, [lang]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return lessons.filter((l) => (level === "all" || l.level === level) &&
      (!q || [l.title, l.subtitle, l.grammarFocus].some((s) => s.toLowerCase().includes(q))));
  }, [lessons, level, query]);

  const currentId = lessons.find((l) => !completed.has(l.id))?.id;
  const doneCount = lessons.filter((l) => completed.has(l.id)).length;
  const groups = ["A1", "A2", "B1"].map((lv) => ({ lv, items: filtered.filter((l) => l.level === lv) })).filter((g) => g.items.length);

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-[#0E382B] tracking-tight">{t("journey.title")}</h1>
          <p className="mt-2 text-[#52635B]">{t("journey.sub")}</p>
          <p className="mt-1 text-sm font-semibold text-[#E46A28]" data-testid="journey-progress-count">{doneCount}/{lessons.length} {t("journey.done")}</p>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52635B]" />
          <input
            data-testid="search-lessons-input"
            value={query}
            placeholder={t("journey.search")}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-9 pr-3 h-11 rounded-full bg-white border border-[#E2DFD7] text-sm outline-none focus:border-[#0E382B] transition-colors"
          />
        </div>
      </div>

      <div className="flex gap-2 mb-10 flex-wrap">
        {LEVELS.map((l) => (
          <button
            key={l.key}
            data-testid={l.testid}
            onClick={() => setLevel(l.key)}
            className={`px-5 py-2 rounded-full text-sm font-medium border transition-colors ${
              level === l.key ? "bg-[#0E382B] text-white border-[#0E382B]" : "bg-white text-[#52635B] border-[#E2DFD7] hover:border-[#B5B0A2]"
            }`}
          >
            {l.label || t(l.labelKey)}
          </button>
        ))}
      </div>

      {groups.length === 0 && (
        <div className="text-center py-16 text-[#52635B]" data-testid="journey-empty"><Lock className="w-6 h-6 mx-auto mb-2" />—</div>
      )}

      <div className="space-y-14">
        {groups.map(({ lv, items }) => {
          const all = lessons.filter((l) => l.level === lv);
          return (
            <div key={lv}>
              <BiomeBanner level={lv} done={all.filter((l) => completed.has(l.id)).length} total={all.length} />
              <div className="relative mt-8 space-y-6">
                <div className="absolute top-0 bottom-0 left-6 md:left-1/2 -translate-x-1/2 border-l-2 border-dashed" style={{ borderColor: (TONE[lv] || TONE.A1).main + "40" }} />
                {items.map((l, i) => (
                  <PathNode key={l.id} l={l} idx={i} done={completed.has(l.id)} current={l.id === currentId} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
